import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRestaurantData } from '../../provider/RestaurantDataContext';
import LanguageSwitcher from './LanguageSwitcher';
import SwipeableTemporaryDrawer, { SwipeableDrawerHandle } from '../sidebar/SwipeableTemporaryDrawer';
import CartComponent from '../cart/CartComponent';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useEffect, useRef, useState } from 'react';
import { ALL_ITMES_PARAM, HOMEPAGE, MENU_CARD_URL, SEARCH_PARAM, SEARCH_URL } from '../../utils/Constants';

type SearchAppBarProps = {
  style?: React.CSSProperties | undefined;
}

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(${theme.spacing(3)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
    [theme.breakpoints.up('sm')]: {
      width: '100%',
    }
  },
  [theme.breakpoints.up('xs')]: {
    marginLeft: theme.spacing(1),
    width: '100%',
  },
}));

export default function SearchAppBar({ style }: SearchAppBarProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const navigate = useNavigate();
  const { restaurantData, restaurantData: { qrMenuProperties }, table, token, live } = useRestaurantData();
  const location = useLocation();
  const swipeableTemporaryDrawerRef = useRef<SwipeableDrawerHandle>(null);
  const leftGridRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const midGridRef = useRef<HTMLDivElement>(null);
  const restaurantNameRef = useRef<HTMLDivElement>(null);
  const restaurantNameWidth = useRef<number>(0);
  const [blockName, setBlockName] = useState<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      if ((restaurantNameRef.current?.clientWidth ?? 0) !== 0) {
        restaurantNameWidth.current = (restaurantNameRef.current?.clientWidth ?? 0);
      }
      let paddingLeft = 0;
      if (menuButtonRef.current) {
        const buttonStyle = getComputedStyle(menuButtonRef.current);
        paddingLeft = parseFloat(buttonStyle.paddingLeft);
      }
      if (midGridRef.current?.clientWidth === 0 && restaurantNameRef.current) {
        setBlockName(true);
      } else if ((leftGridRef.current?.clientWidth ?? 0) + (midGridRef.current?.clientWidth ?? 0) > restaurantNameWidth.current + (menuButtonRef.current?.clientWidth ?? 0) - paddingLeft) {
        setBlockName(false);
      }
    }

    window.addEventListener("resize", handleResize);
    // 👇️ remove the event listener when component unmounts
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (midGridRef.current?.clientWidth === 0) {
      setBlockName(true);
    }
    // location needed when switching from menu to something else
  }, [midGridRef.current?.clientWidth, location]);

  useEffect(() => {
    if (location.pathname.includes(SEARCH_URL)) {
      setSearchInput('');
    }
  }, [location]);

  const clearInputField = () => {
    const searchParam = searchParams.get(SEARCH_PARAM);
    const allItemsParam = searchParams.get(ALL_ITMES_PARAM);
    const relativePath = location.pathname.split(`${HOMEPAGE}/`)[1];
    if (searchParam && searchParam.length > 0) {
      if (allItemsParam === 'false') {
        navigate(`${HOMEPAGE}/${relativePath.substring(0, relativePath.indexOf('/'))}/${MENU_CARD_URL}`);
      } else {
        searchParams.set(SEARCH_PARAM, '');
        setSearchParams(new URLSearchParams(searchParams));
      }
      setSearchInput('');
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const searchVal = event.target.value;
    setSearchInput(searchVal);
    const relativePath = location.pathname.split(`${HOMEPAGE}/`)[1];
    const restaurantId = relativePath.substring(0, relativePath.indexOf('/'));
    const allItemsParam = searchParams.get(ALL_ITMES_PARAM);
    if (searchVal.length || allItemsParam === 'true') {
      navigate(`${HOMEPAGE}/${restaurantId}/${SEARCH_URL}?${SEARCH_PARAM}=${searchVal}${allItemsParam && allItemsParam !== 'null'?`&${ALL_ITMES_PARAM}=${allItemsParam}`:''}`);
    } else {
      navigate(`${HOMEPAGE}/${restaurantId}/${MENU_CARD_URL}`);
    }
  };

  return (
    <Box style={{ ...style }}>
      <SwipeableTemporaryDrawer ref={swipeableTemporaryDrawerRef} />
      <AppBar position="static" sx={{ flexGrow: '1', backgroundImage: 'none' }} enableColorOnDark color="secondary">
        <Toolbar >
          <Grid2 container wrap='nowrap' sx={{ width: '100%' }}>
            <Grid2 ref={leftGridRef} md={4} xs={'auto'} sx={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'nowrap', alignItems: 'center' }}>
              <IconButton
                ref={menuButtonRef}
                size="large"
                edge="start"
                color="inherit"
                aria-label="open drawer"
                sx={{ mr: 0 }}
                onClick={() => { if (swipeableTemporaryDrawerRef.current) swipeableTemporaryDrawerRef.current.toggleDrawer(); }}
              >
                <MenuIcon />
              </IconButton>
              {
                restaurantData.imageUrl && restaurantData.imageUrl.length > 0 &&
                <Box sx={{ display: { xs: qrMenuProperties?.forceLogo ? 'block' : 'none', sm: 'block' } }}>
                  <img src={restaurantData.imageUrl} alt={'RestaurantLogo'} style={{ maxHeight: '3.9vh', paddingRight: '1rem' }} />
                </Box>
              }
              <Typography
                ref={restaurantNameRef}
                variant="h6"
                component="div"
                sx={{ flexGrow: 1, display: blockName ? 'none' : location.pathname.indexOf('/'+MENU_CARD_URL) === -1 ? 'block' : { xs: 'none', sm: 'none', md: 'block' } }}
              >
                {restaurantData.name}
              </Typography>
            </Grid2>
            <Grid2 ref={midGridRef} md={4} xs sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {
                (location.pathname.indexOf(MENU_CARD_URL) !== -1 || location.pathname.indexOf(SEARCH_URL) !== -1) &&
                <Search sx={{ flexGrow: { xs: 0, md: 1 } }}>
                  <SearchIconWrapper sx={{ pl: '0.2rem', pr: '0.2rem' }}>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <StyledInputBase
                    value={searchParams.get(SEARCH_PARAM) ?? searchInput}
                    placeholder={t('searchAppBarInput') ?? undefined}
                    inputProps={{ 'aria-label': 'search' }}
                    onChange={handleOnChange}
                  />
                  <IconButton sx={{ pl: 0 }} onClick={clearInputField} aria-label="Delete Search value">
                    <CloseIcon />
                  </IconButton>
                </Search>
              }
            </Grid2>
            <Grid2 md={4} xs={'auto'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
              {
                qrMenuProperties?.dofEnabled && !live && (table > 0 || token.length > 0) &&
                <CartComponent />
              }
              <Box sx={{ ml: '0.25rem' }}>
                <LanguageSwitcher />
              </Box>
            </Grid2>
          </Grid2>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
